import { useMutation } from "@apollo/client";
import styled from "@emotion/styled";
import React from "react";
import { useNavigate } from "react-router-dom";
import { i18n, mediaQuery, ProductTypeCodeEnum, RootState, setOfferId, setOneTimeToggle, SourceEnum, useAppDispatch, useAppSelector } from "src/common";
import { GetOfferDto } from "src/common/api/apollo/additional";
import { createCheckoutHealthOffer } from 'src/common/api/apollo/supplementary';
import { Button, Divider, DropdownDownIcon, Flex, Loader, Text } from 'src/components';
import { AppRoutesAdditional } from "src/pages/app";
import { colors } from "src/theme";
import { Card } from "../card";
import { CoveragesAdditionalCard } from "./coveragesAdditional.card";
import { OfferAdditionalHeaderCard } from "./offerAdditionalHeader.part";



const DetailsLink = styled.a({
  textDecoration: 'none',
  color: colors.primary,
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  gap: 8,
});

const QuestionnaireLink = styled.div({
  padding: '10px 14px',
  borderRadius: 8,
  backgroundColor: colors.primary4,
});

const OfferAdditionalCardStyle = styled(Card)({
  marginLeft: 16,
  marginRight: 16,
  width: 'auto',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',

  [mediaQuery('laptopL')]: {
    marginLeft: 0,
    marginRight: 0
  },
});

const ButtonStyle = styled(Button)({
  [mediaQuery('laptopL')]: {
    minWidth: 200,
  },
});

export interface OfferAdditionalCardProps {
  offer: GetOfferDto;
  loading?: boolean;
  className?: string;
  showSelectButton?: boolean;
  productTypeCode?: string;
  source?: string;
  onSelectOffer?: (offer: GetOfferDto) => void;
  selectButtonText?: string;
  offerSubtitle?: string;
  offerSubDescription?: string;
}

export const OfferAdditionalCard = ({
  offer,
  loading,
  className,
  showSelectButton = true,
  productTypeCode = ProductTypeCodeEnum.DODATNO,
  source = SourceEnum.DODATNO,
  onSelectOffer,
  selectButtonText = 'Kupi',
  offerSubDescription,
}: OfferAdditionalCardProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [createCheckoutHealthOfferMutation] = useMutation(createCheckoutHealthOffer);
  const customer = useAppSelector((store: RootState) => store.model.customer.data);
  const soIdWeb = useAppSelector((store: RootState) => store.model.offer.soIdWeb);

  if (loading) {
    return (
      <Card radius={12} padding={20} className={className}>
        <Loader />
      </Card>
    )
  }

  const monthlyPrice = ((offer.productTypeSpecific?.calculatedPrices?.total?.generalTransferWithInstallments || offer.productTypeSpecific?.calculatedPrices?.total?.creditCardWithInstallments) ?? 0) / 12;
  const yearlyPrice = offer.productTypeSpecific?.calculatedPrices?.total?.creditCard ?? 0;

  const handleSelectOffer = () => {
    if (onSelectOffer) {
      onSelectOffer(offer);
      return;
    }
    createCheckoutHealthOfferMutation({
      variables: {
        productCode: offer.product?.code,
        birthDate: customer.birthDate,
        email: customer.email,
        phoneNumber: customer.phoneNumber,
        productTypeCode,
        source,
        soId: soIdWeb || undefined
      }
    })
      .then((response) => {
        const offerId = response.data.createCheckoutHealthOffer;
        dispatch(setOfferId(offerId));
        dispatch(setOneTimeToggle(true));
        navigate(`${AppRoutesAdditional.OFFER.fullPath}/${offerId}`);
      })
      .catch((error) => {
        console.error('Greška prilikom izvršavanja mutacije:', error);
      });
  };

  const detailsUrl = typeof offer.product?.annotations?.[0] === 'string'
    ? offer.product.annotations[0].match(/href=["']([^"']+)["']/)?.[1] ?? null
    : null;



  return (
    <OfferAdditionalCardStyle padding={8} className={className} >
      <Flex vertical gap={12}>
        <OfferAdditionalHeaderCard
          monthlyPrice={monthlyPrice}
          yearlyPrice={yearlyPrice}
          priceLabel="/12 mj."
          logoUrl={offer?.product?.brand?.logoUrl ?? ''}
          offerSubtitle={offer?.product?.name}
          offerSubDescription={offerSubDescription ?? "Za osobe od 18 do 70 godina"}
        />
        <Divider dashed />
        <Flex vertical gap={4}>
          <Text type="tiny" color={colors.text4}>{i18n.t('pages.offersAdditionalPage.addedDescription')}</Text>
          <CoveragesAdditionalCard coverages={offer.productTypeSpecific?.additional?.coverages ?? []} fixedCoverages={offer.product?.fixedCoverages ?? []} />
          {offer.product?.brand?.code !== 'agramLife' &&
            <>
              {(!offer.productTypeSpecific?.healthQuestionnaire?.completedAt && !offer.productTypeSpecific?.healthQuestionnaire?.isConfirmed) &&
                <QuestionnaireLink >
                  <Text type="base" bold color={colors.primary}>{i18n.t('pages.offersAdditionalPage.fillQuestionnaire')}</Text>
                </QuestionnaireLink>
              }
              {
                offer.productTypeSpecific?.healthQuestionnaire?.completedAt &&
                <QuestionnaireLink>
                  <Text type="base" bold color={colors.primary}>{i18n.t('pages.offersAdditionalPage.filledQuestionnaire')}</Text>
                </QuestionnaireLink>
              }
            </>
          }
        </Flex>
        <Flex justify='space-between' gap={8}>
          {detailsUrl && (
            <DetailsLink href={detailsUrl ?? ''} target="_blank" rel="noopener noreferrer">
              <Text type="base" bold color={colors.primary}>{i18n.t('pages.offersAdditionalPage.showDetails')}</Text>
              <DropdownDownIcon color={colors.primary} />
            </DetailsLink>
          )}
          {showSelectButton && <ButtonStyle
            btnType="primary"
            onClick={handleSelectOffer}
            text={selectButtonText}
          />}
        </Flex>
      </Flex>
    </OfferAdditionalCardStyle>
  )
};
